'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';

interface LoginFormProps {
  onSubmit: (email: string, password: string) => void;
  isLoading: boolean;
  error: string | null;
}

const LoginForm = ({ onSubmit, isLoading, error }: LoginFormProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const router = useRouter();

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const validateEmail = (value: string): boolean => {
    if (!value) {
      setEmailError('Email or phone is required');
      return false;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^[6-9]\d{9}$/;
    
    if (!emailRegex.test(value) && !phoneRegex.test(value)) {
      setEmailError('Enter a valid email or 10-digit phone number');
      return false;
    }
    
    setEmailError('');
    return true;
  };

  const validatePassword = (value: string): boolean => {
    if (!value) {
      setPasswordError('Password is required');
      return false;
    }
    
    if (value.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      return false;
    }
    
    setPasswordError('');
    return true;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const isEmailValid = validateEmail(email);
    const isPasswordValid = validatePassword(password);
    
    if (isEmailValid && isPasswordValid) {
      onSubmit(email, password);
    }
  };

  const handleEmailChange = (value: string) => {
    setEmail(value);
    if (emailError) validateEmail(value);
  };

  const handlePasswordChange = (value: string) => {
    setPassword(value);
    if (passwordError) validatePassword(value);
  };

  if (!isHydrated) {
    return (
      <div className="w-full max-w-md mx-auto bg-card rounded-lg shadow-elevation-lg p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-10 bg-muted rounded"></div>
          <div className="h-10 bg-muted rounded"></div>
          <div className="h-12 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-md mx-auto bg-card rounded-lg shadow-elevation-lg p-8">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Email/Phone Input */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
            Email or Phone Number
          </label>
          <div className="relative">
            <Icon 
              name="EnvelopeIcon" 
              size={20} 
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <input
              id="email"
              type="text"
              value={email}
              onChange={(e) => handleEmailChange(e.target.value)}
              onBlur={() => validateEmail(email)}
              className={`w-full pl-10 pr-4 py-3 bg-background border rounded-md text-sm focus-ring transition-smooth ${
                emailError ? 'border-error' : 'border-input'
              }`}
              placeholder="Enter email or 10-digit phone"
              disabled={isLoading}
            />
          </div>
          {emailError && (
            <p className="mt-1 text-xs text-error flex items-center gap-1">
              <Icon name="ExclamationCircleIcon" size={14} />
              {emailError}
            </p>
          )}
        </div>

        {/* Password Input */}
        <div>
          <label htmlFor="password" className="block text-sm font-medium text-foreground mb-2">
            Password
          </label>
          <div className="relative">
            <Icon 
              name="LockClosedIcon" 
              size={20} 
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => handlePasswordChange(e.target.value)}
              onBlur={() => validatePassword(password)}
              className={`w-full pl-10 pr-12 py-3 bg-background border rounded-md text-sm focus-ring transition-smooth ${
                passwordError ? 'border-error' : 'border-input'
              }`}
              placeholder="Enter your password"
              disabled={isLoading}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-smooth"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={20} />
            </button>
          </div>
          {passwordError && (
            <p className="mt-1 text-xs text-error flex items-center gap-1">
              <Icon name="ExclamationCircleIcon" size={14} />
              {passwordError}
            </p>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="p-3 bg-error/10 border border-error rounded-md">
            <p className="text-sm text-error flex items-center gap-2">
              <Icon name="ExclamationTriangleIcon" size={18} />
              {error}
            </p>
          </div>
        )}

        {/* Forgot Password Link */}
        <div className="flex justify-end">
          <Link
            href="/forgot-password"
            className="text-sm text-primary hover:underline transition-smooth"
          >
            Forgot Password?
          </Link>
        </div>

        {/* Login Button */}
        <button
          type="submit"
          disabled={isLoading}
          className="w-full py-3 bg-primary text-primary-foreground rounded-md font-medium text-sm transition-smooth hover:shadow-elevation-md hover-lift disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {isLoading ? (
            <>
              <Icon name="ArrowPathIcon" size={18} className="animate-spin" />
              <span>Logging in...</span>
            </>
          ) : (
            <>
              <Icon name="ArrowRightOnRectangleIcon" size={18} />
              <span>Login</span>
            </>
          )}
        </button>

        {/* Create Account Link */}
        <div className="text-center">
          <p className="text-sm text-muted-foreground">
            Don't have an account?{' '}
            <Link
              href="/register"
              className="text-primary font-medium hover:underline transition-smooth"
            >
              Create Account
            </Link>
          </p>
        </div>
      </form>
    </div>
  );
};

export default LoginForm;